import { useEffect, useState } from "react"
import React from "react";
import { CurrencyInput, EInputTypes, InputColumnTableProps, InputTable, InputTextCustom, formatCurrency } from "medere-ui-framework"
import '../Budget.scss'
import { ExtrasBudgetDetailDTO, defaultExtrasBudgetDetailDTO, getSubtotalValueDay, getSubtotalValueMonth } from "../../../dto/budget/BudgetDetailDTO";
import { InputTableContainerProps } from "../../../dto/InputTableContainerProps";
import { BudgetBodyComponent } from "./BudgetBodyComponent";

interface ExtrasTableProps extends InputTableContainerProps {
    items: ExtrasBudgetDetailDTO[];
}

export default function ExtrasTable({items, addToTotal, duration, CRUDBehaviour=true, showPriceByItem=true, showEarnings}: ExtrasTableProps) {

    const [newExtrasBudgetDetailDTO, setNewExtrasBudgetDetailDTO] = useState<ExtrasBudgetDetailDTO>(defaultExtrasBudgetDetailDTO);
    const [subtotalValueDay, setSubtotalValueDay] = useState<number>(0);
    const [subtotalValueMonth, setSubtotalValueMonth] = useState<number>(0);

    const calculateTotal = (unitsqty: number, unitprice: number) => {
        return unitsqty * unitprice;
    };

    function calculateItem(item: ExtrasBudgetDetailDTO): ExtrasBudgetDetailDTO {
        return {
            ...item,
            valueday: duration ? ((duration > 0) ? (calculateTotal(item.unitsqty, item.unitprice) / duration) : 0) : 0,
            valuemonth: calculateTotal(item.unitsqty, item.unitprice)
        };
    }

    useEffect(() => {
        setNewExtrasBudgetDetailDTO(prev => ({...prev, valueday: duration ? ((duration > 0) ? prev.valuemonth / duration : 0) : 0}));
        let subtotal = 0;
        items.forEach((item) => {
            item.valuemonth = calculateTotal(item.unitsqty, item.unitprice);
            item.valueday = duration ? ((duration > 0) ? item.valuemonth / duration : 0) : 0;
            subtotal += item.valuemonth;
        });
        setSubtotalValueMonth(subtotal);
    }, [items, duration]);

    useEffect(() => {
        setNewExtrasBudgetDetailDTO(prev => ({
            ...prev,
            valuemonth: calculateTotal(prev.unitsqty, prev.unitprice)
        }));
    }, [newExtrasBudgetDetailDTO.unitsqty, newExtrasBudgetDetailDTO.unitprice]);

    useEffect(() => {
        setNewExtrasBudgetDetailDTO(prev => ({...prev, valueday: duration ? ((duration > 0) ? prev.valuemonth / duration : 0) : 0}));
    }, [newExtrasBudgetDetailDTO.valuemonth, duration]);

    useEffect(() => {
        setSubtotalValueDay(duration ? ((duration > 0) ? subtotalValueMonth / duration : 0) : 0);
    }, [subtotalValueMonth, duration]);

    const columnsTable: InputColumnTableProps<ExtrasBudgetDetailDTO>[] = React.useMemo(() => [{
        body: (item) => <span className="nv-truncate nv-tip" data-pr-tooltip={item.description}>{item.description}</span>,
        className: "columnStart",
        header: "Item",
        input: (_item) =>
            <InputTextCustom
                value={newExtrasBudgetDetailDTO.description}
                placeholder="Escribir extra"
                inputType={EInputTypes.TEXT}
                onChange={(e: any) => {
                    setNewExtrasBudgetDetailDTO(prev => ({...prev, description: e.target.value}));
                }}
            />,
        footerClassName: "font-bold",
        footer: "Subtotales",
    }, {
        className: "tableColumn justify-content-center",
        header: "Unidades",
        input: (_item) =>
            <InputTextCustom
                value={newExtrasBudgetDetailDTO.unitsqty}
                inputType={EInputTypes.NUMBER}
                inputClassName="text-center"
                onChange={(e: any) => {
                    setNewExtrasBudgetDetailDTO(prev => ({...prev, unitsqty: e.target.value ?? 0}));
                }}
            />,
        body: (item, index) =>
            <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} inputType={EInputTypes.NUMBER} value={item.unitsqty} inputClassName="text-center" onChange={(e: any) => {
                onHandleEditBudget(index, calculateItem({...item, unitsqty: e.target.value ?? 0}));
            }} />
    }, {
        className: "columnPrice",
        header: <p className="mr-2">{showEarnings ? "Costo unitario" : "Valor unitario"}</p>,
        input: (_item) =>
            <CurrencyInput ammount={newExtrasBudgetDetailDTO.unitprice} setAmmount={(newValue: number | null) => {
                setNewExtrasBudgetDetailDTO(prev => ({...prev, unitprice: newValue ?? prev.unitprice ?? 0}));
            }} />,
        bodyClassName: "justify-content-end",
        body: (item, index) =>
            <BudgetBodyComponent CRUDBehaviour={CRUDBehaviour} currency value={item.unitprice} pClassName="mr-2" pValue={showPriceByItem ? formatCurrency(item.unitprice as number) : ''} onChange={(newValue: number | null) => {
                onHandleEditBudget(index, calculateItem({...item, unitprice: newValue ?? 0}));
            }} />
    }, {
        className: "columnPrice w-8",
        header: "Valor día",
        input: (_item) => <p>{formatCurrency(newExtrasBudgetDetailDTO.valueday as number)}</p>,
        bodyClassName: "font-bold justify-content-end",
        body: (item) => showPriceByItem ? formatCurrency(item.valueday as number) : '',
        footerClassName: "font-bold",
        footer: formatCurrency(subtotalValueDay as number),
        show: () => duration > 0
    }, {
        className: "columnPrice w-8 pr-2",
        header: "Valor total",
        input: (_item) => <p>{formatCurrency(newExtrasBudgetDetailDTO.valuemonth as number)}</p>,
        bodyClassName: "font-bold justify-content-end",
        body: (item) => showPriceByItem ? formatCurrency(item.valuemonth as number) : '',
        footerClassName: "font-bold",
        footer: formatCurrency(subtotalValueMonth as number)
    }] as InputColumnTableProps<ExtrasBudgetDetailDTO>[], [duration, newExtrasBudgetDetailDTO, subtotalValueDay, subtotalValueMonth, showPriceByItem, showEarnings]);

    function onHandleAddBudget(): void {
        if (newExtrasBudgetDetailDTO.description === '') return;
        if (newExtrasBudgetDetailDTO.unitsqty === 0) return;
        items.push(newExtrasBudgetDetailDTO);
        const updatedValueMonth = getSubtotalValueMonth(items);
        if (addToTotal)
            addToTotal(updatedValueMonth - subtotalValueMonth);
        setSubtotalValueMonth(updatedValueMonth);
        setSubtotalValueDay(getSubtotalValueDay(items));
        setNewExtrasBudgetDetailDTO(defaultExtrasBudgetDetailDTO);
    }

    function onHandleDeleteBudget(index: number): void {
        items.splice(index, 1);
        const updatedValueMonth = getSubtotalValueMonth(items);
        if (addToTotal)
            addToTotal(updatedValueMonth - subtotalValueMonth);
        setSubtotalValueMonth(updatedValueMonth);
        setSubtotalValueDay(getSubtotalValueDay(items));
    }

    function onHandleEditBudget(index: number, newItemValue: ExtrasBudgetDetailDTO): void {
        const valueMonthToAdd = newItemValue.valuemonth - items[index].valuemonth;
        items.splice(index, 1, newItemValue);
        const updatedValueMonth = subtotalValueMonth + valueMonthToAdd;
        setSubtotalValueDay(duration ? ((duration > 0) ? (updatedValueMonth / duration) : 0) : 0);
        setSubtotalValueMonth(updatedValueMonth);
        if (addToTotal) addToTotal(valueMonthToAdd);
    }

    return (
        <div className="nv-card budget-table-card">
            <span className="tableTitle"> Extras </span>
            <InputTable divider dividerClassName="my-0 border-top-1" dividerInFirstItem columns={columnsTable} items={items} onAdd={CRUDBehaviour ? onHandleAddBudget : undefined} onDelete={CRUDBehaviour ? (_item, index) => onHandleDeleteBudget(index) : undefined} headerDivClassName="tableHeader align-items-center" inputDivClassName={"align-items-center"} footerDivClassName="tableFooter" divBtnClassName="max-w-54 min-w-54" />
        </div>
    );
}
